import { CheckIconBold, ChevronDownIcon } from '@assets/icons';
import {
  Box,
  Button,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Stack,
  Text
} from '@chakra-ui/react';
import { omit } from 'lodash-es';
import { useMemo } from 'react';

const RESKIN_MENU_BUTTON_PROPS = {
  h: 'auto',
  w: 'auto',
  iconSpacing: -1,
  fontWeight: 450,
  fontSize: 'sm',
  p: 2,
  pl: 4,
  ml: 'auto',
  variant: 'reskinGhost',
  bgColor: 'brandPrimary.containerLow',
  color: 'brandPrimary.onContainer',
  iconSize: 4,
  textProps: { m: 0 }
};

/** @typedef {Object} StyledSelectOption
 * @property {string} value
 * @property {string} label
 * @property {JSX.Element} icon
 */

/**
 * @typedef {Object} StyledSelectProps
 * @property {StyledSelectOption[]} options
 * @property {string} placeholder
 * @property {string} [value]
 * @property {(value: string) => void} onChange
 * @property {"reskin"|undefined} variant
 * @property {import('@chakra-ui/react').MenuProps} props
 * @property {string} [selectLabel]
 * @property {import('@chakra-ui/react').ButtonProps} [menuButtonProps]
 */

/**
 * @param {StyledSelectProps} props
 */
export default function StyledSelect({
  placeholder,
  selectLabel,
  value,
  onChange,
  options,
  variant,
  menuButtonProps = {},
  menuListProps = {},
  menuItemProps = {},
  ...menuProps
}) {
  const selectedOption = useMemo(
    () => options?.find((option) => option.value === value),
    [options, value]
  );

  if (variant === 'reskin') {
    menuButtonProps = { ...RESKIN_MENU_BUTTON_PROPS, ...menuButtonProps };
  }

  return (
    <Menu
      variant='select'
      {...menuProps}
    >
      <MenuButton
        h='60px'
        w='full'
        as={Button}
        rightIcon={
          <Icon
            as={ChevronDownIcon}
            boxSize={menuButtonProps?.iconSize ?? 6}
          />
        }
        variant='reskinWhite'
        p={4}
        rounded='lg'
        color='surface.onSurfaceVariant'
        fontWeight={400}
        {...omit(menuButtonProps, ['textProps', 'iconSize'])}
      >
        <HStack
          gap={3}
          w='full'
        >
          {selectedOption?.icon && <Box>{selectedOption?.icon}</Box>}
          <Stack
            gap={0}
            w='full'
          >
            {selectLabel && (
              <Text
                fontSize='11px'
                textAlign='left'
              >
                {selectLabel}
              </Text>
            )}
            <Text
              fontSize='16px'
              textAlign='left'
              layerStyle='ellipsis-text'
              mt={1}
              pr={3}
              {...menuButtonProps?.textProps}
            >
              {selectedOption?.label || placeholder}
            </Text>
          </Stack>
        </HStack>
      </MenuButton>
      <MenuList {...menuListProps}>
        {options?.map((option) => {
          const isSelected = option.value === value;

          return (
            <MenuItem
              key={option.value}
              onClick={() => onChange(option.value)}
              {...menuItemProps}
            >
              {option.icon}
              {option.label}
              <Spacer />
              {isSelected && (
                <Icon
                  as={CheckIconBold}
                  color='brandPrimary.primary'
                />
              )}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}
